import axiosInstance from '../../../core/services/axiosConfig';
import { apis } from '../../../core/services/params';
import React, { useState, useEffect } from "react";
import { Modal, Descriptions, Spin, Typography, Card, Badge, List, Row, Col, Statistic, Tag, Divider, Tooltip, Button, message } from "antd";
import {
    PhoneOutlined,
    UserOutlined,
    CalendarOutlined,
    SolutionOutlined,
    FileTextOutlined,
    CustomerServiceOutlined,
    InfoCircleOutlined,
    ClockCircleOutlined,
    CheckCircleOutlined,
    WarningOutlined,
    DatabaseOutlined,
    SyncOutlined,
    CopyOutlined,
    DownloadOutlined,
} from "@ant-design/icons";
import moment from "moment";

const { Paragraph } = Typography;

interface OzonetelCallDetailsProps {
    visible: boolean;
    ucid: string;
    onClose: () => void;
}

interface CallDetailsData {
    call_analysis: {
        _id: string;
        analysis: {
            action_items: string[];
            call_metadata: {
                call_purpose: string;
                issue_resolved: string;
            };
            entities: {
                issue_category: string;
                item_id: string | null;
                order_id: string | null;
                suggested_issue_category: string;
            };
            executive_summary: string;
            next_best_action: string;
            priority: string;
            sentiment_and_profiling: {
                agent_professionalism_score: number;
                agent_tone: string;
                customer_satisfaction: string;
                customer_tone: string;
            };
            transcript: Array<{
                speaker: string;
                text: string;
                timestamp: string;
            }>;
        };
        audio_url: string;
        created_at: string;
        updated_at: string;
        ucid: string;
    };
    live_report: {
        Agent: string;
        AgentID: string;
        AgentStatus: string;
        AudioFile: string;
        CallDuration: string;
        CallerID: string;
        CampaignName: string;
        Comments: string | null;
        CustomerRingTime: string;
        CustomerStatus: string;
        DID: string;
        DialStatus: string;
        DialedNumber: string;
        Disposition: string;
        Duration: string;
        E164: string;
        EndTime: string;
        FallBackRule: string;
        HandlingTime: string;
        HangupBy: string;
        HoldDuration: string;
        Location: string | null;
        QueueTime: string;
        RateID: string | null;
        Rating: string;
        RatingComments: string | null;
        SNO: number;
        Skill: string;
        SkillID: string | null;
        StartTime: string;
        Status: string;
        TimeToAnswer: string;
        TransferType: string | null;
        TransferredTo: string | null;
        Type: string;
        UCID: string;
        UUI: string | null;
        WrapUpDuration: string;
        _id: string;
        monitorUCID: string;
        ivr_feedback?: {
            OptionResponse?: 'Satisfied' | 'Not_Satisfied' | 'No_input' | string;
        };
    };
}

const OzonetelCallDetails: React.FC<OzonetelCallDetailsProps> = ({ visible, ucid, onClose }) => {
    const [loading, setLoading] = useState(false);
    const [reAnalyzing, setReAnalyzing] = useState(false);
    const [data, setData] = useState<CallDetailsData | null>(null);

    useEffect(() => {
        if (visible && ucid) {
            fetchCallDetails(ucid);
        } else {
            setData(null);
        }
    }, [visible, ucid]);

    const fetchCallDetails = async (ucid: string) => {
        setLoading(true);
        try {
            const response = await axiosInstance.get(`${apis.OZONETEL_LIVE_REPORT_DETAILS}?ucid=${ucid}`);
            const result = response.data;
            if (result.status) {
                setData(result.data);
            }
        } catch (error) {
            console.error("Error fetching call details:", error);
        } finally {
            setLoading(false);
        }
    };

    // @@ AI-COMMENT: Re-analyze the call audio and refresh data on success
    const reAnalyze = async () => {
        if (!data) return;
        const audioUrl = data.live_report?.AudioFile || data.call_analysis?.audio_url;
        setReAnalyzing(true);
        try {
            const response = await axiosInstance.post(apis.OZONETEL_ANALYZE_CALL_AUDIO, {
                url: audioUrl,
                ucid,
                re_analyze: true,
            });
            if (response.data?.status !== false) {
                message.success("Re-analysis triggered successfully. Refreshing data...");
                await fetchCallDetails(ucid);
            } else {
                message.error(response.data?.message || "Re-analysis failed.");
            }
        } catch (error) {
            console.error("Error re-analyzing call:", error);
            message.error("Failed to trigger re-analysis. Please try again.");
        } finally {
            setReAnalyzing(false);
        }
    };

    // @@ AI-COMMENT: Build plain-text transcript from analysis.transcript for copy/download
    const buildTranscriptText = (): string => {
        if (!data) return '';
        const transcript = data.call_analysis?.analysis?.transcript || [];
        const lines: string[] = [
            `Call Transcript — UCID: ${ucid}`,
            `Start: ${data.live_report?.StartTime}  End: ${data.live_report?.EndTime}`,
            '─'.repeat(60),
        ];
        transcript.forEach((entry) => {
            const time = entry.timestamp ? ` [${entry.timestamp}]` : '';
            lines.push(`${entry.speaker}${time}: ${entry.text}`);
        });
        return lines.join('\n');
    };

    // @@ AI-COMMENT: Copy transcript to clipboard with toast feedback
    const handleCopyTranscript = async () => {
        const text = buildTranscriptText();
        if (!text) { message.warning('No transcript available'); return; }
        await navigator.clipboard.writeText(text);
        message.success('Transcript copied to clipboard');
    };

    // @@ AI-COMMENT: Download transcript as a named .txt file
    const handleDownloadTranscript = () => {
        const text = buildTranscriptText();
        if (!text) { message.warning('No transcript available'); return; }
        const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `transcript_${ucid}_${(data?.live_report?.StartTime || '').replace(/[: ]/g, '-')}.txt`;
        a.click();
        URL.revokeObjectURL(url);
    };

    if (!visible) return null;

    const renderSentimentBar = (score: number) => (
        <div className="flex items-center gap-2 w-full">
            <div className="flex-1 bg-gray-200 rounded-full h-2">
                <div
                    className={`h-2 rounded-full ${score >= 4 ? 'bg-green-500' : score >= 3 ? 'bg-blue-500' : 'bg-red-500'}`}
                    style={{ width: `${(score / 5) * 100}%` }}
                />
            </div>
            <span className="text-xs font-semibold w-6 text-right">{score}/5</span>
        </div>
    );

    return (
        <Modal
            title={
                <div className="flex justify-between items-center pr-8">
                    <div className="flex items-center gap-2 text-lg">
                        <PhoneOutlined /> Call Details <Tag color="blue" className="ml-2 text-base">{ucid}</Tag>
                    </div>
                    {/* @@ AI-COMMENT: Re-analyze button triggers analysis API and refreshes details */}
                    <Button
                        icon={<SyncOutlined spin={reAnalyzing} />}
                        loading={reAnalyzing}
                        onClick={reAnalyze}
                        disabled={!data || loading}
                        size="small"
                        type="default"
                        style={{ marginRight: 8 }}
                    >
                        Re-analyze
                    </Button>
                </div>
            }
            open={visible}
            onCancel={onClose}
            width="98%"
            style={{ top: 20 }}
            footer={null}
            bodyStyle={{ padding: '16px', height: 'calc(100vh - 100px)', overflowY: 'auto', backgroundColor: '#f5f7fa' }}
            maskClosable={false}
        >
            {loading ? (
                <div className="flex justify-center items-center h-full">
                    <Spin size="large" tip="Loading comprehensive call details..." />
                </div>
            ) : data ? (
                <div className="space-y-4">
                    {/* Header Stats Row */}
                    <Card size="small" className="shadow-sm border-0">
                        <Row gutter={24} align="middle">
                            <Col span={3}>
                                <Statistic title="Duration" value={data.live_report?.CallDuration} prefix={<ClockCircleOutlined />} valueStyle={{ fontSize: '1.25rem' }} />
                            </Col>
                            <Col span={3}>
                                <Statistic
                                    title="Priority"
                                    value={data.call_analysis?.analysis?.priority}
                                    valueStyle={{ color: data.call_analysis?.analysis?.priority === 'High' ? '#cf1322' : '#389e0d', fontSize: '1.25rem' }}
                                />
                            </Col>
                            <Col span={4}>
                                <Statistic
                                    title="Issue Resolved"
                                    value={data.call_analysis?.analysis?.call_metadata?.issue_resolved}
                                    prefix={data.call_analysis?.analysis?.call_metadata?.issue_resolved === 'Yes' ? <CheckCircleOutlined className="text-green-500" /> : <WarningOutlined className="text-orange-500" />}
                                    valueStyle={{ fontSize: '1.25rem' }}
                                />
                            </Col>
                            <Col span={4}>
                                <Statistic title="Agent" value={data.live_report?.Agent} prefix={<UserOutlined />} valueStyle={{ fontSize: '1.25rem' }} />
                            </Col>
                            <Col span={4}>
                                <Statistic
                                    title="Customer"
                                    value={data.live_report?.CallerID}
                                    prefix={<PhoneOutlined />}
                                    valueStyle={{ fontSize: '1.25rem' }}
                                    formatter={(value) => (
                                        <a href={`tel:${value}`} className="text-gray-900 hover:text-blue-600 hover:underline">
                                            {String(value).replace(/(\d{5})(\d{5})/, "$1 $2")}
                                        </a>
                                    )}
                                />
                            </Col>
                            <Col span={6}>
                                <div className="flex flex-col h-full justify-center">
                                    <span className="text-gray-500 text-xs uppercase mb-1">Call Time</span>
                                    <div className="text-sm font-medium">{moment(data.live_report?.StartTime).format("MMM DD, YYYY HH:mm:ss")}</div>
                                </div>
                            </Col>
                        </Row>
                        {/* IVR Feedback Option Response */}
                        {data.live_report?.ivr_feedback?.OptionResponse && (
                            <div style={{ margin: '16px 0 0 0', padding: '10px 14px', background: '#f6f8fa', borderRadius: 6, display: 'inline-block' }}>
                                <strong>IVR Feedback:</strong> {data.live_report.ivr_feedback.OptionResponse.replace('_', ' ')}
                            </div>
                        )}
                    </Card>

                    <Row gutter={16} className="h-full">
                        {/* Left Column: AI Analysis */}
                        <Col span={7} className="h-full">
                            <div className="space-y-4">
                                <Card title={<><SolutionOutlined /> Executive Summary</>} size="small" className="shadow-sm border-gray-200">
                                    <Paragraph className="text-gray-700 text-sm mb-0 text-justify">
                                        {data.call_analysis?.analysis?.executive_summary}
                                    </Paragraph>
                                </Card>

                                <Card title={<><FileTextOutlined /> Call Context & Entities</>} size="small" className="shadow-sm border-gray-200">
                                    <Descriptions column={1} size="small" bordered className="text-xs">
                                        <Descriptions.Item label="Call Purpose">
                                            <span className="whitespace-pre-wrap">{data.call_analysis?.analysis?.call_metadata?.call_purpose}</span>
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Issue Category">
                                            <Tag color="cyan">{data.call_analysis?.analysis?.entities?.issue_category}</Tag>
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Suggested Issue">
                                            <Tag color="geekblue">{data.call_analysis?.analysis?.entities?.suggested_issue_category}</Tag>
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Item ID">
                                            {data.call_analysis?.analysis?.entities?.item_id || <span className="text-gray-400">N/A</span>}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Order ID">
                                            {data.call_analysis?.analysis?.entities?.order_id || <span className="text-gray-400">N/A</span>}
                                        </Descriptions.Item>
                                    </Descriptions>
                                </Card>

                                <Card title={<><CalendarOutlined /> Action Items & Next Steps</>} size="small" className="shadow-sm border-gray-200">
                                    <List
                                        size="small"
                                        split={false}
                                        dataSource={data.call_analysis?.analysis?.action_items}
                                        renderItem={item => (
                                            <List.Item className="px-0 py-1">
                                                <div className="flex items-start gap-2">
                                                    <Badge status="processing" className="mt-1.5" />
                                                    <span className="text-sm text-gray-700 leading-snug">{item}</span>
                                                </div>
                                            </List.Item>
                                        )}
                                    />
                                    <Divider className="my-3" />
                                    <div className="bg-blue-50 p-3 rounded border border-blue-100">
                                        <div className="text-xs font-bold text-blue-800 uppercase mb-1">Next Best Action</div>
                                        <div className="text-sm text-blue-900 leading-snug">
                                            {data.call_analysis?.analysis?.next_best_action}
                                        </div>
                                    </div>
                                </Card>

                                <Card title={<><UserOutlined /> Sentiment & Profiling</>} size="small" className="shadow-sm border-gray-200">
                                    <div className="space-y-4">
                                        <div>
                                            <div className="flex justify-between text-xs mb-1">
                                                <span className="text-gray-500">Agent Professionalism</span>
                                            </div>
                                            {renderSentimentBar(data.call_analysis?.analysis?.sentiment_and_profiling?.agent_professionalism_score)}
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <span className="text-xs text-gray-500 block">Agent Tone</span>
                                                <Tag color="default">{data.call_analysis?.analysis?.sentiment_and_profiling?.agent_tone}</Tag>
                                            </div>
                                            <div>
                                                <span className="text-xs text-gray-500 block">Customer Tone</span>
                                                <Tag color={data.call_analysis?.analysis?.sentiment_and_profiling?.customer_tone === 'Angry' ? 'red' : 'blue'}>
                                                    {data.call_analysis?.analysis?.sentiment_and_profiling?.customer_tone}
                                                </Tag>
                                            </div>
                                            <div className="col-span-2">
                                                <span className="text-xs text-gray-500 block">Customer Satisfaction</span>
                                                <span className="font-semibold text-gray-800">{data.call_analysis?.analysis?.sentiment_and_profiling?.customer_satisfaction}</span>
                                            </div>
                                        </div>
                                    </div>
                                </Card>
                            </div>
                        </Col>

                        {/* Middle Column: Live Report Technical Details */}
                        <Col span={7} className="h-full">
                            <div className="space-y-4 h-full flex flex-col">
                                <Card title={<><InfoCircleOutlined /> Technical Details</>} size="small" className="shadow-sm border-gray-200 flex-1">
                                    <Descriptions column={1} size="small" bordered className="text-xs">
                                        <Descriptions.Item label="Campaign">{data.live_report?.CampaignName}</Descriptions.Item>
                                        <Descriptions.Item label="Skill used">{data.live_report?.Skill || '-'}</Descriptions.Item>
                                        <Descriptions.Item label="DID / Dialed">{data.live_report?.DID} / {data.live_report?.DialedNumber}</Descriptions.Item>
                                        <Descriptions.Item label="Status">
                                            <Tag color={data.live_report?.Status === 'Answered' ? 'green' : 'red'}>{data.live_report?.Status}</Tag>
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Dial Status">{data.live_report?.DialStatus}</Descriptions.Item>
                                        <Descriptions.Item label="Hangup By">{data.live_report?.HangupBy}</Descriptions.Item>
                                        <Descriptions.Item label="Agent ID">{data.live_report?.AgentID}</Descriptions.Item>
                                        <Descriptions.Item label="Agent Status">{data.live_report?.AgentStatus}</Descriptions.Item>

                                        <Descriptions.Item label="Start Time">{data.live_report?.StartTime}</Descriptions.Item>
                                        <Descriptions.Item label="End Time">{data.live_report?.EndTime}</Descriptions.Item>
                                        <Descriptions.Item label="Duration (Total)">{data.live_report?.Duration}</Descriptions.Item>
                                        <Descriptions.Item label="Time To Answer">{data.live_report?.TimeToAnswer}</Descriptions.Item>
                                        <Descriptions.Item label="Customer Ring">{data.live_report?.CustomerRingTime}</Descriptions.Item>
                                        <Descriptions.Item label="Queue Time">{data.live_report?.QueueTime}</Descriptions.Item>
                                        <Descriptions.Item label="Hold Duration">{data.live_report?.HoldDuration}</Descriptions.Item>
                                        <Descriptions.Item label="WrapUp Duration">{data.live_report?.WrapUpDuration}</Descriptions.Item>

                                        <Descriptions.Item label="Disposition" span={1}>
                                            <Tooltip title={data.live_report?.Disposition}>
                                                <span className="truncate block max-w-[150px]">{data.live_report?.Disposition}</span>
                                            </Tooltip>
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Monitor UCID" span={1}>
                                            <span className="text-xs text-gray-500 break-all">{data.live_report?.monitorUCID}</span>
                                        </Descriptions.Item>
                                    </Descriptions>
                                </Card>

                                <Card title={<><DatabaseOutlined /> System Info</>} size="small" className="shadow-sm border-gray-200">
                                    <Descriptions column={1} size="small" bordered className="text-xs">
                                        <Descriptions.Item label="Analysis ID">
                                            <span className="text-xs text-gray-500 break-all">{data.call_analysis?._id}</span>
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Live Report ID">
                                            <span className="text-xs text-gray-500 break-all">{data.live_report?._id}</span>
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Created At">
                                            {moment(data.call_analysis?.created_at).format("YYYY-MM-DD HH:mm:ss")}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Updated At">
                                            {moment(data.call_analysis?.updated_at).format("YYYY-MM-DD HH:mm:ss")}
                                        </Descriptions.Item>
                                    </Descriptions>
                                </Card>
                            </div>
                        </Col>

                        {/* Right Column: Transcript & Recording */}
                        <Col span={10} className="h-full">
                            <div className="flex flex-col h-full space-y-4">
                                {(data.live_report?.AudioFile || data.call_analysis?.audio_url) && (
                                    <Card size="small" className="shadow-sm border-gray-200 bg-gray-50">
                                        <div className="flex items-center gap-3">
                                            <CustomerServiceOutlined className="text-lg text-blue-600" />
                                            <div className="flex-1">
                                                <div className="text-xs font-semibold uppercase text-gray-500 mb-1">Call Recording</div>
                                                <audio controls src={data.live_report?.AudioFile || data.call_analysis?.audio_url} className="w-full h-8" />
                                            </div>
                                        </div>
                                    </Card>
                                )}

                                <Card
                                    title="Transcript"
                                    extra={
                                        <div className="flex items-center gap-2">
                                            {/* @@ AI-COMMENT: Copy transcript button */}
                                            <Tooltip title="Copy Transcript">
                                                <button
                                                    onClick={handleCopyTranscript}
                                                    className="flex items-center gap-1 px-2 py-1 text-xs rounded border border-gray-200 bg-white hover:bg-gray-50 text-gray-500 hover:text-blue-600 transition-colors"
                                                >
                                                    <CopyOutlined />
                                                    <span>Copy</span>
                                                </button>
                                            </Tooltip>
                                            {/* @@ AI-COMMENT: Download transcript as .txt button */}
                                            <Tooltip title="Download as .txt">
                                                <button
                                                    onClick={handleDownloadTranscript}
                                                    className="flex items-center gap-1 px-2 py-1 text-xs rounded border border-gray-200 bg-white hover:bg-gray-50 text-gray-500 hover:text-green-600 transition-colors"
                                                >
                                                    <DownloadOutlined />
                                                    <span>.txt</span>
                                                </button>
                                            </Tooltip>
                                        </div>
                                    }
                                    size="small"
                                    className="shadow-sm border-gray-200 flex-1 flex flex-col overflow-hidden"
                                    bodyStyle={{ flex: 1, overflowY: 'auto', padding: '12px' }}
                                >
                                    <div className="space-y-4">
                                        {data.call_analysis?.analysis?.transcript?.map((entry, index) => (
                                            <div key={index} className={`flex flex-col ${['Moglix Agent', 'Agent', 'speaker1'].includes(entry.speaker) ? 'items-end' : 'items-start'}`}>
                                                <div className={`max-w-[90%] rounded-lg p-3 shadow-sm ${['Moglix Agent', 'Agent', 'speaker1'].includes(entry.speaker)
                                                    ? 'bg-blue-600 text-white rounded-tr-none'
                                                    : 'bg-white text-gray-800 border border-gray-200 rounded-tl-none'
                                                    }`}>
                                                    <div className={`flex justify-between items-center mb-1 gap-4 text-xs ${['Moglix Agent', 'Agent', 'speaker1'].includes(entry.speaker) ? 'text-blue-100' : 'text-gray-500'
                                                        }`}>
                                                        <span className="font-bold">{entry.speaker}</span>
                                                        <span className="opacity-75">{entry.timestamp || ''}</span>
                                                    </div>
                                                    <p className="text-sm m-0 leading-relaxed whitespace-pre-wrap">{entry.text}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </Card>
                            </div>
                        </Col>
                    </Row>
                </div>
            ) : (
                <div className="text-center py-20 text-gray-400">
                    No details available for this call.
                </div>
            )}
        </Modal>
    );
};

export default OzonetelCallDetails;
